import { h } from 'preact';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { USER_CLICK, USER_FULLSCREEN, USER_KEYBOARD, USER_PIP, USER_SET_FOCUS } from '../../../../../types';
import { loopThroughSubtitlesTracks } from '../../../../../utils/tracks';
import { BUTTON_LAYER } from '../../../../common/constants';
import { useNewStore, usePlayerContext, useStyles } from '../../../../common/hooks';
import { isEscapeKeyboard, isValidateKeyboard } from '../../../../common/utils/keyboard';
import { withBreakPoints } from '../../../hoc';
import { Button } from '../../common';
import { CockpitBubble } from '../../common/cockpitBubble';
import { COCKPIT_BUBBLE_WRAPPER_STYLE } from '../../common/cockpitBubble/constants';
import { Icon } from '../../common/svg/icons';
import EcoButton from '../extra/EcoButton';
import EmbedButton from '../extra/EmbedButton';
import Settings from '../extra/Settings';
import SourceButton from '../extra/SourceButton';
import Volume from './volume/Volume';
import { PIANO_POSITION_PRINCIPAL } from '../../../../../monitoring/piano/types';

function ExpControl({
  medium,
  extraSmall,
  small,
  mobile,
  ios
}) {
  const player = usePlayerContext();
  const {
    isFullscreen, panelLiveOption: { show: isPanelOpened },
    waitStart, reporter, originUrl, controlIconSize
  } = useNewStore('ui', player);
  const { pipSupported } = useNewStore('systemInfo', player);
  const volumeState = useNewStore('volume', player);
  const { isAd } = useNewStore('ad', player);
  const { textTrack, subtitleSelected, subtitlesAvailable } = useNewStore('subtitles', player);
  const { isDAI, isTv, isLive, isDVR, embed } = useNewStore('media', player);
  const { comingNext } = useNewStore('settings', player);
  const { hasQualities } = useNewStore('playback', player);

  const fullScreenIconRef = useRef(null);
  const setStyles = useStyles();

  const [subtitlesLabel, setSubtitlesLabel] = useState('');

  useEffect(() => {
    const withIcon = (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
          minHeight: controlIconSize
        }}
      >
        <div>{`Sous-titres - ${textTrack.label}`}</div>
        {textTrack.icon && (
          <Icon
            name={textTrack.icon}
            width={controlIconSize}
            height={controlIconSize}
            color="white"
          />
        )}
        {!mobile && <div>(c)</div>}
      </div>
    );
    setSubtitlesLabel(withIcon);
  }, [textTrack.icon, textTrack.label, mobile, controlIconSize]);

  const handleFullScreenClick = useCallback((e) => {
    const isKeyboardEvent = e instanceof KeyboardEvent;
    const isValidateKey = isValidateKeyboard(e);
    if (isKeyboardEvent && !isValidateKey && !isEscapeKeyboard(e)) {
      return;
    }

    player.userEvents$.next({
      action: USER_FULLSCREEN,
      value: !isFullscreen,
      source: isKeyboardEvent ? USER_KEYBOARD : USER_CLICK
    });

    player.fullscreen(!isFullscreen);

    if (!isFullscreen) {
      player.userEvents$.next({
        action: USER_SET_FOCUS,
        payload: { element: fullScreenIconRef.current }
      });
    }
  }, [isFullscreen, isPanelOpened, isAd]);

  return (
    <div style={setStyles(COCKPIT_BUBBLE_WRAPPER_STYLE)}>
      <CockpitBubble>
        <Button
          enableBackground
          hidden={(isAd && !isDAI) || (!subtitlesAvailable.length)}
          ariaLabel={textTrack.ariaLabel}
          label={subtitlesLabel}
          onClick={(e) => loopThroughSubtitlesTracks({
            player,
            currentIndex: subtitleSelected,
            available: subtitlesAvailable,
            position: PIANO_POSITION_PRINCIPAL,
            source: e instanceof KeyboardEvent ? USER_KEYBOARD : USER_CLICK
          })}
          xs
          name="btn-subtitles"
          type="icon"
          icon={subtitleSelected === -1 ? 'tracks' : 'tracks-active'}
          zIndex={BUTTON_LAYER + 1}
        />

        <Volume
          enableBackground
          volume={volumeState.level}
          muted={volumeState.muted}
          medium={medium}
          extraSmall={extraSmall}
          small={small}
          name="btn-volume"
          disableFocus={false}
          ariaLabel={`${volumeState.muted ? 'volume désactivé' : 'volume activé'} raccourci touche m`}
          ariaHidden="false"
        />
      </CockpitBubble>
      <CockpitBubble hidden={extraSmall}>
        <SourceButton hidden={isAd || small || medium || !originUrl} />
        <EcoButton hidden={isAd || waitStart || !hasQualities} />
        <EmbedButton hidden={isAd || waitStart || !embed} />
        <Settings hidden={isAd || waitStart || ((isLive || isDVR) && (!comingNext.enabled && !hasQualities && !reporter))} />
      </CockpitBubble>
      <CockpitBubble>
        <Button
          enableBackground
          hidden={!pipSupported || (ios && !pipSupported) || isAd || isTv}
          label="Lecteur réduit (i)"
          onClick={() => {
            player.userEvents$.next({
              action: USER_PIP,
              value: true,
              source: USER_CLICK
            });
            player.requestPIP(true);
          }}
          xs
          name="btn-pip"
          type="icon"
          icon="pip"
          disableFocus
          zIndex={BUTTON_LAYER}
        />

        <Button
          enableBackground
          hidden={isTv}
          label={`${isFullscreen ? 'Quitter le plein écran' : 'Plein écran'} (f)`}
          ariaLabel={`${isFullscreen ? 'Quitter le mode plein écran' : 'Plein écran'} raccourci touche f`}
          ariaHidden="false"
          iconRef={fullScreenIconRef}
          onClick={handleFullScreenClick}
          last
          xs
          name="btn-fullscreen"
          disableFocus={false}
          zIndex={BUTTON_LAYER + 1} // +1 so it can be clicked is button panel is visible
          type="icon"
          icon={isFullscreen ? 'fullscreen-on' : 'fullscreen-off'}
        />
      </CockpitBubble>
    </div>
  );
}

export default withBreakPoints(ExpControl);
